import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { ArrowLeft, Users, Trophy, Target, Clock, Activity, TrendingUp, Calendar, User } from 'lucide-react';
import DateRangeBar from './DateRangeBar';
import { queryDateParams } from '../utils/dateRange';
import './TeamDetail.css';

function TeamDetail({ token, teamName, nightMode, onBack, onPlayerClick, initialRange }) {
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [range, setRange] = useState(initialRange || { preset: 'all', dateFrom: '', dateTo: '' });

  useEffect(() => {
    fetchTeamDetails();
  }, [teamName, range.dateFrom, range.dateTo]);

  const fetchTeamDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/teams/${encodeURIComponent(teamName)}/details`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { fuzzy: 'true', ...queryDateParams(range.dateFrom, range.dateTo) }
        }
      );
      setTeamData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Team details fetch error:', error);
      setError('Failed to fetch team details: ' + (error.response?.data?.message || error.message));
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const teamAliases = teamData?.aliases || [teamName];

  const isOurTeam = (name) => teamAliases.some((alias) => alias.toLowerCase() === String(name || '').toLowerCase());

  const getMatchResult = (match) => {
    const isTeamA = isOurTeam(match.teamA);
    const ourScore = isTeamA ? match.scoreA : match.scoreB;
    const theirScore = isTeamA ? match.scoreB : match.scoreA;
    
    if (ourScore > theirScore) return 'win';
    if (theirScore > ourScore) return 'loss';
    return 'tie';
  };

  const getFormClass = (result) => {
    switch(result) {
      case 'W': return 'form-win';
      case 'L': return 'form-loss';
      case 'T': return 'form-tie';
      default: return '';
    }
  };

  if (loading && !teamData) {
    return (
      <div className={`team-detail ${nightMode ? 'night-mode' : ''}`}>
        <div className="loading">Loading team details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`team-detail ${nightMode ? 'night-mode' : ''}`}>
        <div className="team-detail-header">
          <button onClick={onBack} className="back-btn">
            <ArrowLeft size={20} /> Back to Statistics
          </button>
          <h1>Team Details</h1>
        </div>
        <div className="error-message">
          {error}
          <button onClick={fetchTeamDetails} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!teamData) {
    return (
      <div className={`team-detail ${nightMode ? 'night-mode' : ''}`}>
        <div className="team-detail-header">
          <button onClick={onBack} className="back-btn">
            <ArrowLeft size={20} /> Back to Statistics
          </button>
          <h1>Team Details</h1>
        </div>
        <div className="empty-state">
          <p>No data available for this team.</p>
        </div>
      </div>
    );
  }

  const winLossData = [
    { name: 'Wins', value: teamData.stats.wins, color: '#28a745' },
    { name: 'Losses', value: teamData.stats.losses, color: '#dc3545' },
    { name: 'Ties', value: teamData.stats.ties, color: '#ffc107' }
  ];

  const monthlyData = teamData.monthlyPerformance.map(month => ({
    month: month.month,
    matches: month.matches,
    wins: month.wins,
    losses: month.losses,
    winRate: parseFloat(month.winPercentage)
  }));

  const extraAliases = (teamData.aliases || []).filter((alias) => alias !== teamData.teamName);
  const roster = teamData.teamInfo?.players || [];

  return (
    <div className={`team-detail ${nightMode ? 'night-mode' : ''}`}>
      <div className="team-detail-header">
        <button onClick={onBack} className="back-btn">
          <ArrowLeft size={20} /> Back to Statistics
        </button>
        <div className="team-title">
          <h1 style={{ color: teamData.teamInfo.color }}>{teamData.teamName}</h1>
          <span className="team-subtitle">{teamData.stats.totalMatches} matches in this range</span>
          {extraAliases.length ? (
            <span className="team-subtitle">
              Including similar names: {extraAliases.join(', ')}
            </span>
          ) : null}
        </div>
      </div>

      <DateRangeBar
        preset={range.preset}
        dateFrom={range.dateFrom}
        dateTo={range.dateTo}
        onChange={setRange}
      />

      <div className="team-tabs">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab-btn ${activeTab === 'matches' ? 'active' : ''}`}
          onClick={() => setActiveTab('matches')}
        >
          Match History
        </button>
        <button 
          className={`tab-btn ${activeTab === 'players' ? 'active' : ''}`}
          onClick={() => setActiveTab('players')}
        >
          Players ({roster.length})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'opponents' ? 'active' : ''}`}
          onClick={() => setActiveTab('opponents')}
        >
          Head-to-Head
        </button>
      </div>

      {activeTab === 'overview' && (
        <div className="overview-tab">
          <div className="stats-summary">
            <div className="stat-card">
              <div className="stat-icon">
                <Trophy size={24} />
              </div>
              <div className="stat-content">
                <h3>{teamData.stats.totalMatches}</h3>
                <p>Total Matches</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon win">
                <Target size={24} />
              </div>
              <div className="stat-content">
                <h3>{teamData.stats.winPercentage}%</h3>
                <p>Win Rate</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <Activity size={24} />
              </div>
              <div className="stat-content">
                <h3>+{teamData.stats.pointDifferential}</h3>
                <p>Point Differential</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <Clock size={24} />
              </div>
              <div className="stat-content">
                <h3>{formatTime(teamData.stats.totalTime)}</h3>
                <p>Total Play Time</p>
              </div>
            </div>
          </div>

          <div className="charts-grid">
            <div className="chart-container">
              <h3>Win/Loss Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={winLossData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {winLossData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-container">
              <h3>Performance Trend</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={monthlyData}>

                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="winRate" stroke="#007bff" name="Win Rate %" />
                  <Line type="monotone" dataKey="matches" stroke="#28a745" name="Matches" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="recent-form-container">
              <h3>Recent Form (Last 10 matches)</h3>
              <div className="form-display">
                {teamData.stats.recentForm.split('').map((result, index) => (
                  <span key={index} className={`form-indicator ${getFormClass(result)}`}>
                    {result}
                  </span>
                ))}
              </div>
              <p className="form-legend">
                <span className="form-win">W</span> = Win, 
                <span className="form-loss">L</span> = Loss, 
                <span className="form-tie">T</span> = Tie
              </p>
            </div>

            <div className="detailed-stats">
              <h3>Detailed Statistics</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">Wins</span>
                  <span className="stat-value">{teamData.stats.wins}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Losses</span>
                  <span className="stat-value">{teamData.stats.losses}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Ties</span>
                  <span className="stat-value">{teamData.stats.ties}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Points For</span>
                  <span className="stat-value">{teamData.stats.pointsFor}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Points Against</span>
                  <span className="stat-value">{teamData.stats.pointsAgainst}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Avg Points For</span>
                  <span className="stat-value">{teamData.stats.avgPointsFor}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Avg Points Against</span>
                  <span className="stat-value">{teamData.stats.avgPointsAgainst}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'matches' && (
        <div className="matches-tab">
          <h2>Match History</h2>
          <div className="matches-list">
            {teamData.matches.map((match, index) => {
              const result = getMatchResult(match);
              const isTeamA = isOurTeam(match.teamA);
              const opponent = isTeamA ? match.teamB : match.teamA;
              const ourScore = isTeamA ? match.scoreA : match.scoreB;
              const theirScore = isTeamA ? match.scoreB : match.scoreA;
              
              return (
                <div key={index} className={`match-item ${result}`}>
                  <div className="match-date">
                    <Calendar size={16} />
                    {formatDate(match.date)}
                  </div>
                  <div className="match-teams">
                    <span className="team-name">{teamName}</span>
                    <span className="vs">vs</span>
                    <span className="opponent-name">{opponent}</span>
                  </div>
                  <div className="match-score">
                    <span className={`score ${result}`}>
                      {ourScore} - {theirScore}
                    </span>
                  </div>
                  <div className="match-time">
                    <Clock size={14} />
                    {formatTime(match.time)}
                  </div>
                  {match.matchGroup && (
                    <div className="match-group">
                      {match.matchGroup.name}
                    </div>
                  )}
                  {match.note && (
                    <div className="match-note">
                      {match.note}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === 'players' && (
        <div className="players-tab">
          <h2>Team Roster</h2>
          {roster.length === 0 ? (
            <div className="empty-state">
              <p>No players added to this team yet.</p>
            </div>
          ) : (
            <div className="players-grid">
              {roster.map((player, index) => (
                <div 
                  key={player._id || index} 
                  className="player-card"
                  onClick={() => onPlayerClick && onPlayerClick(teamData.teamInfo._id, player._id)}
                >
                  <div className="player-icon">
                    <User size={24} />
                  </div>
                  <div className="player-info">
                    <h4>{player.name}</h4>
                    {player.number && <span className="player-number">#{player.number}</span>}
                    {player.position && <span className="player-position">{player.position}</span>}
                  </div>
                  <div className="player-status">
                    <span className={`status ${player.active ? 'active' : 'inactive'}`}>
                      {player.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'opponents' && (
        <div className="opponents-tab">
          <h2>Head-to-Head Records</h2>
          <div className="opponents-table">
            <table>
              <thead>
                <tr>
                  <th>Opponent</th>
                  <th>Matches</th>
                  <th>Wins</th>
                  <th>Losses</th>
                  <th>Ties</th>
                  <th>Win %</th>
                  <th>Points For</th>
                  <th>Points Against</th>
                  <th>Avg Score</th>
                </tr>
              </thead>
              <tbody>
                {teamData.opponents.map((opponent, index) => (
                  <tr key={index}>
                    <td className="opponent-name">{opponent.name}</td>
                    <td>{opponent.matches}</td>
                    <td className="wins">{opponent.wins}</td>
                    <td className="losses">{opponent.losses}</td>
                    <td className="ties">{opponent.ties}</td>
                    <td className="win-percentage">{opponent.winPercentage}%</td>
                    <td>{opponent.pointsFor}</td>
                    <td>{opponent.pointsAgainst}</td>
                    <td>
                      {(opponent.pointsFor / opponent.matches).toFixed(1)} - {(opponent.pointsAgainst / opponent.matches).toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamDetail;
